import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { 
  BookOpen, 
  Video, 
  MessageSquare, 
  Award, 
  TrendingUp, 
  Calendar,
  Clock,
  CheckCircle,
  Brain,
  Target,
  Play,
  Send,
  User,
  Star,
  Trophy
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import ClassScheduleViewer from '../../components/ClassScheduleViewer';
import TutorAvailabilityCardView from '../../components/TutorAvailabilityCardView';
import { mockClasses, mockQuizzes, mockProgressData, mockBadges } from '../../mockData/dashboardData';
import { LineChart, Line, BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { supabase } from '../../supabaseClient';
import ProfileView from '../../components/ProfileView';
import MessagesPage from './MessagesPage';

// ============================================
// EXTRACTED COMPONENT - MessagesView (outside main component to prevent re-creation)
// ============================================
const MessagesView = React.memo(({ messageText, handleMessageTextChange }) => {
  const mockMessages = [
    {
      id: 1,
      from: 'Dr. Priya Sharma',
      role: 'Math Tutor',
      message: 'Great job on your last quiz!',
      timestamp: '2 hours ago',
      unread: true
    },
    {
      id: 2,
      from: 'Prof. Rajesh Verma',
      role: 'Physics Tutor',
      message: 'Please review the homework I sent.',
      timestamp: '1 day ago',
      unread: false
    }
  ];

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Messages List */}
      <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Messages</h2>
        <div className="space-y-3">
          {mockMessages.map((msg) => (
            <div
              key={msg.id}
              className={`border rounded-xl p-4 cursor-pointer hover:border-cyan-300 dark:hover:border-cyan-600 transition-colors ${
                msg.unread ? 'border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-gray-700' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{msg.from}</h3>
                    {msg.unread && <span className="w-2 h-2 bg-cyan-600 rounded-full"></span>}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{msg.role}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">{msg.message}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{msg.timestamp}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Message Thread */}
      <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-2xl shadow-lg flex flex-col h-[600px]">
        <div className="border-b border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white">Dr. Priya Sharma</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Math Tutor</p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div className="flex-1">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-tl-none p-4">
                <p className="text-sm text-gray-900 dark:text-white">Great job on your last quiz! Keep up the good work.</p>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">2 hours ago</p>
            </div>
          </div>

          <div className="flex items-start gap-3 flex-row-reverse">
            <div className="w-8 h-8 bg-cyan-600 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 text-right">
              <div className="bg-cyan-600 text-white rounded-2xl rounded-tr-none p-4 inline-block">
                <p className="text-sm">Thank you! I'm working hard on the next topics.</p>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">1 hour ago</p>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Type your message..."
              value={messageText}
              onChange={handleMessageTextChange}
              className="flex-1"
            />
            <Button className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

const StudentDashboard = () => {
  const { user, getAccessToken } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [messageText, setMessageText] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [scheduleViewMode, setScheduleViewMode] = useState('class-schedule'); // 'class-schedule' or 'tutor-availability'
  const [selectedTutorIdForAvailability, setSelectedTutorIdForAvailability] = useState(null);

  const upcomingClasses = mockClasses.filter(c => c.status === 'scheduled');
  const completedClasses = mockClasses.filter(c => c.status === 'completed');
  const pendingQuizzes = mockQuizzes.filter(q => q.status === 'pending');
  const completedQuizzes = mockQuizzes.filter(q => q.status === 'completed');

  // Fetch tasks and schedules from backend
  useEffect(() => {
    fetchTasks();
    fetchSchedules();

    // Set up real-time subscription
    const subscription = supabase
      .channel('student_schedules_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_schedules'
        },
        (payload) => {
          console.log('Schedule change detected:', payload);
          fetchSchedules();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchTasks = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      // Students should use /api/tasks endpoint
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/tasks`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendTasks = await response.json();
        console.log('Fetched tasks:', backendTasks);
        setTasks(backendTasks);
      } else {
        console.warn('Failed to fetch tasks:', await response.text());
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const token = getAccessToken();
      if (!token) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const backendSchedules = await response.json();
        console.log('Fetched schedules:', backendSchedules);
        setSchedules(backendSchedules);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  // Stable callback handler to prevent input re-mounting
  const handleMessageTextChange = useCallback((e) => {
    setMessageText(e.target.value);
  }, []);

  const stats = [
    {
      label: 'Classes Completed',
      value: completedClasses.length,
      change: '+3 this week',
      icon: CheckCircle,
      color: 'from-green-400 to-emerald-500'
    },
    {
      label: 'Average Score',
      value: '85%',
      change: '+5% from last month',
      icon: TrendingUp,
      color: 'from-blue-400 to-cyan-500'
    },
    {
      label: 'Quizzes Pending',
      value: pendingQuizzes.length,
      change: 'Complete soon',
      icon: Brain,
      color: 'from-purple-400 to-pink-500'
    },
    {
      label: 'Badges Earned',
      value: mockBadges.filter(b => b.earned).length,
      change: `${mockBadges.length - mockBadges.filter(b => b.earned).length} to unlock`,
      icon: Award,
      color: 'from-yellow-400 to-orange-500'
    }
  ];

  const subjectPerformance = [
    { subject: 'Math', score: 85 },
    { subject: 'Science', score: 78 },
    { subject: 'English', score: 92 },
    { subject: 'History', score: 88 },
    { subject: 'Physics', score: 82 }
  ];

  const mockMessages = [
    {
      id: 1,
      from: 'Dr. Priya Sharma',
      role: 'Tutor',
      message: 'Great job on your last quiz! Keep up the good work.',
      timestamp: '2 hours ago',
      unread: true
    },
    {
      id: 2,
      from: 'Prof. Rajesh Verma',
      role: 'Tutor',
      message: 'Your next physics session is scheduled for tomorrow.',
      timestamp: '5 hours ago',
      unread: false
    }
  ];

  // Overview Content
  const DashboardOverview = () => {
    // Filter today's classes - account for timezone conversion
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const todaysClasses = schedules.filter(schedule => {
      const scheduleDateTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
      const scheduleDateStr = `${scheduleDateTime.getFullYear()}-${String(scheduleDateTime.getMonth() + 1).padStart(2, '0')}-${String(scheduleDateTime.getDate()).padStart(2, '0')}`;
      return scheduleDateStr === todayStr;
    });

    // Function to check if Join Class button should be enabled
    const canJoinClass = (schedule) => {
      if (!schedule.meeting_link) return false;
      
      const now = new Date();
      const scheduleDateTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
      const classEndTime = new Date(scheduleDateTime.getTime() + schedule.duration_minutes * 60000);
      const tenMinsBefore = new Date(scheduleDateTime.getTime() - 10 * 60000);
      
      return now >= tenMinsBefore && now <= classEndTime;
    };
    
    return (

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Upcoming Classes & Progress */}
          <div className="lg:col-span-2 space-y-6">
          {/* Today's Classes - Real Data */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Calendar className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
                Today's Classes
              </h2>
            </div>
            <div className="space-y-4">
              {todaysClasses.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No classes scheduled for today</p>
                </div>
              ) : (
                todaysClasses.map((schedule) => {
                  const task = tasks.find(t => t.id === schedule.task_id);
                  const startTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
                  const isJoinable = canJoinClass(schedule);
                
                  return (
                    <div key={schedule.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-cyan-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white">{task?.title || 'Class'}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{task?.subject || 'Subject N/A'}</p>
                        </div>
                        <span className="px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded-full">
                          Today
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Video className="w-4 h-4" />
                          <span>{schedule.duration_minutes} min</span>
                        </div>
                      </div>
                      {schedule.meeting_link ? (
                        <Button 
                          className={`w-full ${isJoinable ? 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700' : 'bg-gray-400 cursor-not-allowed'} text-white`}
                          onClick={() => isJoinable && window.open(schedule.meeting_link, '_blank')}
                          disabled={!isJoinable}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          {isJoinable ? 'Join Class' : 'Not Yet Available'}
                        </Button>
                      ) : (
                        <Button className="w-full bg-gray-400 text-white cursor-not-allowed" disabled>
                          <Play className="w-4 h-4 mr-2" />
                          No Meeting Link
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Upcoming Classes - Real Data */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              Upcoming Classes
            </h2>
            <div className="space-y-4">
              {(() => {
                // Filter upcoming classes (future dates)
                const now = new Date();
                const upcomingSchedules = schedules.filter(schedule => {
                  const scheduleDateTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
                  return scheduleDateTime > now;
                }).sort((a, b) => {
                  const dateA = new Date(`${a.scheduled_date}T${a.scheduled_time}Z`);
                  const dateB = new Date(`${b.scheduled_date}T${b.scheduled_time}Z`);
                  return dateA - dateB;
                }).slice(0, 3); // Show only next 3 classes

                if (upcomingSchedules.length === 0) {
                  return (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <BookOpen className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No upcoming classes scheduled</p>
                    </div>
                  );
                }

                return upcomingSchedules.map((schedule) => {
                  const task = tasks.find(t => t.id === schedule.task_id);
                  const startTime = new Date(`${schedule.scheduled_date}T${schedule.scheduled_time}Z`);
                  
                  return (
                    <div key={schedule.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-blue-300 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white">{task?.title || 'Class'}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{task?.subject || 'Subject N/A'}</p>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-xs font-semibold rounded-full">
                          {startTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mb-3 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Video className="w-4 h-4" />
                          <span>{schedule.duration_minutes} min</span>
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Progress Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
              Your Progress
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={mockProgressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" stroke="#6b7280" />
                <YAxis stroke="#6b7280" />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#0891b2" strokeWidth={3} dot={{ fill: '#0891b2', r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Column - Quizzes & Badges */}
        <div className="space-y-6">
          {/* Pending Quizzes */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Brain className="w-6 h-6 text-purple-600" />
              Pending Quizzes
            </h2>
            <div className="space-y-3">
              {pendingQuizzes.map((quiz) => (
                <div key={quiz.id} className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:border-purple-300 transition-colors">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{quiz.title}</h3>
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <span>{quiz.questions} questions</span>
                    <span>{quiz.duration} min</span>
                  </div>
                  <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                    Start Quiz
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Badges */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-yellow-600" />
              Your Badges
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {mockBadges.map((badge) => (
                <div
                  key={badge.id}
                  className={`border-2 rounded-xl p-4 text-center ${
                    badge.earned ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-700' : 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-700'
                  }`}
                >
                  <div className="text-3xl mb-2">{badge.icon}</div>
                  <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-1">{badge.name}</h4>
                </div>
              ))}
            </div>
          </div>

          {/* AI Assistant */}
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl p-6 shadow-lg text-white">
            <h3 className="text-lg font-bold mb-2">Need Help?</h3>
            <p className="text-sm text-cyan-100 mb-4">Ask our AI Tutor Assistant anything!</p>
            <Button className="w-full bg-white text-cyan-600 hover:bg-gray-50 dark:hover:bg-gray-700">
              <MessageSquare className="w-4 h-4 mr-2" />
              Chat with AI
            </Button>
          </div>
        </div>
      </div>

  );
  };

  // My Classes View - Now with Calendar
  // Get unique tutor IDs from tasks for student
  const tutorIds = [...new Set(tasks.map(t => t.tutor_id).filter(Boolean))];

  // Update selected tutor when tasks load
  useEffect(() => {
    if (tutorIds.length > 0 && !selectedTutorIdForAvailability) {
      setSelectedTutorIdForAvailability(tutorIds[0]);
    }
  }, [tutorIds, selectedTutorIdForAvailability]);

  const MyClassesView = () => (
    <div className="space-y-6">
      {/* View Mode Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setScheduleViewMode('class-schedule')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              scheduleViewMode === 'class-schedule'
                ? 'bg-cyan-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Calendar className="w-4 h-4" />
            My Class Schedule
          </button>
          <button
            onClick={() => setScheduleViewMode('tutor-availability')}
            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
              scheduleViewMode === 'tutor-availability'
                ? 'bg-cyan-600 text-white shadow-md'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Clock className="w-4 h-4" />
            Tutor&apos;s Availability
          </button>
        </div>
      </div>

      {/* Class Schedule View */}
      {scheduleViewMode === 'class-schedule' && (
        <ClassScheduleViewer role="student" />
      )}

      {/* Tutor Availability View */}
      {scheduleViewMode === 'tutor-availability' && (
        <div className="space-y-4">
          {tutorIds.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 shadow-lg text-center">
              <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Tutors Assigned</h3>
              <p className="text-gray-500 dark:text-gray-400">
                No tutors are assigned to you yet.
              </p>
            </div>
          ) : (
            <>
              {/* Tutor Selector */}
              {tutorIds.length > 1 && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Select Tutor to View Availability</h3>
                  <div className="flex flex-wrap gap-2">
                    {tasks.filter((t, i, arr) => 
                      arr.findIndex(task => task.tutor_id === t.tutor_id) === i
                    ).map(task => (
                      <button
                        key={task.tutor_id}
                        onClick={() => setSelectedTutorIdForAvailability(task.tutor_id)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          selectedTutorIdForAvailability === task.tutor_id
                            ? 'bg-cyan-600 text-white shadow-md'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {task.subject} Tutor
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tutor Availability Display - Card View */}
              {selectedTutorIdForAvailability && (
                <TutorAvailabilityCardView key={selectedTutorIdForAvailability} tutorId={selectedTutorIdForAvailability} />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  // Quizzes View
  const QuizzesView = () => (
    <div className="space-y-6">
      {/* Pending Quizzes */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <Brain className="w-6 h-6 text-purple-600" />
          Pending Quizzes ({pendingQuizzes.length})
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pendingQuizzes.map((quiz) => (
            <div key={quiz.id} className="border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-gray-700 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-2">{quiz.title}</h3>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                <div className="flex items-center justify-between">
                  <span>Questions:</span>
                  <span className="font-semibold">{quiz.questions}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Duration:</span>
                  <span className="font-semibold">{quiz.duration} min</span>
                </div>
              </div>
              <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white">
                <Play className="w-4 h-4 mr-2" />
                Start Quiz
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Completed Quizzes */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-green-600" />
          Completed Quizzes ({completedQuizzes.length})
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Quiz Name</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Score</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Date</th>
              </tr>
            </thead>
            <tbody>
              {completedQuizzes.map((quiz) => (
                <tr key={quiz.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="py-3 px-4 font-medium">{quiz.title}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="text-lg font-bold text-green-600">{quiz.score}%</span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                      Completed
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">2025-01-10</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Progress View
  const ProgressView = () => (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
          Overall Progress Trend
        </h2>
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={mockProgressData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Line type="monotone" dataKey="score" stroke="#0891b2" strokeWidth={3} dot={{ fill: '#0891b2', r: 6 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Subject-wise Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-purple-600" />
            Subject Performance
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsBarChart data={subjectPerformance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="subject" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Bar dataKey="score" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>

        {/* Achievements & Badges */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-yellow-600" />
            Achievements & Badges
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600 dark:text-gray-400">Badges Earned</span>
              <span className="text-2xl font-bold text-yellow-600">{mockBadges.filter(b => b.earned).length}</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {mockBadges.map((badge) => (
                <div key={badge.id} className="border-2 border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-700 rounded-xl p-3 text-center">
                  <div className="text-3xl mb-1">{badge.icon}</div>
                  <h4 className="text-xs font-semibold text-gray-900 dark:text-white">{badge.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl p-6 shadow-lg text-white">
        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Brain className="w-6 h-6" />
          AI Learning Insights
        </h3>
        <div className="space-y-3">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">📈 Strong Performance</p>
            <p className="text-sm text-purple-100">You're excelling in English! Keep up the great work.</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">💡 Improvement Opportunity</p>
            <p className="text-sm text-purple-100">Focus more on Science concepts - practice problems recommended.</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-sm font-semibold mb-1">🎯 Study Recommendation</p>
            <p className="text-sm text-purple-100">Schedule extra Math revision before the upcoming test.</p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout role="student" activeView={activeView} onViewChange={setActiveView}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {activeView === 'dashboard' && `Welcome back, ${user?.name || 'Student'}! 👋`}
            {activeView === 'classes' && 'My Class Schedule'}
            {activeView === 'quizzes' && 'Quizzes'}
            {activeView === 'progress' && 'Progress'}
            {activeView === 'messages' && 'Messages'}
            {activeView === 'profile' && 'My Profile'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {activeView === 'dashboard' && 'Ready to continue your learning journey?'}
            {activeView === 'classes' && 'View your scheduled classes and upcoming sessions'}
            {activeView === 'quizzes' && 'Take quizzes and track your scores'}
            {activeView === 'progress' && 'Track your learning progress and achievements'}
            {activeView === 'messages' && 'Connect with your tutors'}
            {activeView === 'profile' && 'Manage your profile information'}
          </p>
        </div>

        {/* Stats Grid - Show only on dashboard */}
        {activeView === 'dashboard' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{stat.value}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{stat.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{stat.change}</p>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Dynamic Content Based on Active View */}
        {activeView === 'dashboard' && <DashboardOverview />}
        {activeView === 'classes' && <MyClassesView />}
        {activeView === 'quizzes' && <QuizzesView />}
        {activeView === 'progress' && <ProgressView />}
        {activeView === 'messages' && <MessagesPage />}
        {activeView === 'profile' && <ProfileView />}
      </div>
    </DashboardLayout>
  );
};

export default StudentDashboard;
